import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { SEGMENTS, SegmentDashboard } from '../pages/DashboardsPage';
import { useT } from '../i18n';

/* AutoDashboardShowcase — live demo of what KITXENS builds for clients.
   Auto-rotates through segment dashboards every 8s; pauses on hover. */

export default function AutoDashboardShowcase() {
  const { lang } = useT();
  const es = lang === 'es';
  const segments = SEGMENTS(es);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % segments.length), 8000);
    return () => clearInterval(t);
  }, [paused, segments.length]);

  const seg = segments[idx];

  return (
    <section
      className="bg-[#0B0D12] py-14 lg:py-20"
      data-testid="auto-dashboard-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-[#FF9B4A]">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
              <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
            </span>
            {es ? 'Demo en vivo' : 'Live demo'}
          </p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-white tracking-[-0.015em]">
            {es ? 'Así se ve un dashboard KITXENS — funcionando.' : 'This is what a KITXENS dashboard looks like — running.'}
          </h2>
          <p className="mt-3 text-neutral-400 text-[15px] leading-relaxed">
            {es
              ? 'Un ejemplo de lo que nuestros equipos humanos + IA montan para cada tipo de operación. Cambia solo cada pocos segundos — pasa el cursor para pausar.'
              : 'An example of what our human + AI teams build for every kind of operation. It rotates on its own every few seconds — hover to pause.'}
          </p>
        </div>

        {/* Segment tabs */}
        <div className="mt-7 flex flex-wrap gap-2" role="tablist">
          {segments.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setIdx(i)}
              role="tab"
              aria-selected={i === idx}
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-[12.5px] font-semibold transition-all ${
                i === idx
                  ? 'bg-[#F77F11] border-[#F77F11] text-white'
                  : 'bg-white/[0.04] border-white/15 text-neutral-400 hover:border-[#F77F11]/60 hover:text-white'
              }`}
              data-testid={`showcase-tab-${s.key}`}
            >
              <s.icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          ))}
        </div>

        <div className="mt-5">
          <AnimatePresence mode="wait">
            <SegmentDashboard seg={seg} es={es} />
          </AnimatePresence>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to="/contact?type=discovery"
            className="inline-flex items-center gap-2 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white px-6 h-11 font-semibold text-[14px] transition-colors"
            data-testid="showcase-cta-discovery"
          >
            {es ? 'Diseñemos tu dashboard' : "Let's design your dashboard"} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/services/dashboards"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-[#F77F11] hover:text-[#FF9B4A] text-neutral-300 px-6 h-11 font-semibold text-[14px] transition-colors"
            data-testid="showcase-cta-dashboards"
          >
            {es ? 'Ver todos los ejemplos' : 'See all examples'}
          </Link>
          <span className="text-[11.5px] text-neutral-600">
            {es ? '* Datos de demostración — tu dashboard se conecta a tus datos reales.' : '* Demo data — your dashboard connects to your real data.'}
          </span>
        </div>
      </div>
    </section>
  );
}
